/* eslint-disable indent */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import Button from 'components/Button';
import Container from 'components/Container';
import Heading from 'components/Heading';
import Map from 'components/Map';
import PropertyList from 'components/PropertyList';
import SearchDetailsBar from 'components/SearchDetailsBar';
import Section from 'components/Section';

import {
    useGetResortPropertiesQuery,
    useGetResortQuery,
} from 'store/queries/hotels';

function transformPropertiesToClusterPoints(
    properties,
    countrySlug,
    destinationSlug,
) {
    return properties.map((x) => ({
        type: 'Feature',
        properties: {
            cluster: false,
            id: x.id,
            name: x.name,
            location: x.resort.name,
            countrySlug,
            destinationSlug,
            propertySlug: x.slug,
            thumbnail: x.image ?? null,
            lowestPrice: x.lowestPrice ? x.lowestPrice.amount / 100 : null,
        },
        geometry: {
            type: 'Point',
            coordinates: [parseFloat(x.longitude), parseFloat(x.latitude)],
        },
    }));
}

const PropertyListPage = ({ className }) => {
    const { countrySlug, destinationSlug, resortSlug } = useParams();
    const [mapOpen, toggleMap] = useState(false);
    const [points, updatePoints] = useState(null);

    const { data: resort, isSuccess: resortSuccess } = useGetResortQuery(
        { countrySlug, destinationSlug, resortSlug },
        {
            skip: !countrySlug,
        },
    );
    const {
        isSuccess: propertiesSuccess,
        data: properties,
    } = useGetResortPropertiesQuery(
        { countrySlug, destinationSlug, resortSlug },
        {
            skip: !resortSuccess,
        },
    );

    useEffect(() => {
        if (propertiesSuccess && properties && properties.length > 0) {
            updatePoints(
                transformPropertiesToClusterPoints(
                    properties,
                    countrySlug,
                    destinationSlug,
                ),
            );
        }
    }, [propertiesSuccess, properties, countrySlug, destinationSlug]);

    return (
        <div className={className}>
            <SearchDetailsBar />
            <Container>
                <Section>
                    <div className="heading-section">
                        <Heading element="h1" size="h3" margin="0">
                            {resort && <span>Hotels in {resort.name}</span>}
                        </Heading>
                        {points ? (
                            <div className="image-map-trigger-container">
                                <div
                                    className="image-map-trigger"
                                    onClick={() => {
                                        toggleMap(!mapOpen);
                                    }}
                                >
                                    <div className="trigger">
                                        <Button fake>
                                            {mapOpen ? 'Hide' : 'Show'} map
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </Section>

                <Section>
                    {points && properties && mapOpen ? (
                        <div className="map-container">
                            <Map
                                defaultZoom={10}
                                defaultCenter={{
                                    lat: properties[0].latitude,
                                    lng: properties[0].longitude,
                                }}
                                points={points}
                                isInteractive
                                scrollTo
                            />
                        </div>
                    ) : null}
                    <div>
                        {propertiesSuccess && properties ? (
                            <PropertyList
                                items={properties.map((property) => ({
                                    id: property.id,
                                    title: property.name,
                                    subtitle: property.resort.name,
                                    image: [...property.images].sort(
                                        (
                                            { image_order: a },
                                            { image_order: b },
                                        ) => a - b,
                                    )[0]?.url,
                                    url: `/hotels/${property.resort.destination.country.slug}/${property.resort.destination.slug}/${property.slug}.html`,
                                }))}
                            />
                        ) : null}
                    </div>
                </Section>
            </Container>
        </div>
    );
};

export default PropertyListPage;
